import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useGetCurrentUserQuery } from "../../app/accounts/authuser";
import {
  ACTIVE_PROJECTS_LIMIT,
  useGetProjectsQuery,
} from "../../app/services/projects";
import {
  dispatchShortcutAction,
  getAltDigit,
  getAltProjectDirection,
  isTypingTarget,
} from "../../utils/keyboardShortcuts";
import { isUuid } from "../../utils/uuid";

/**
 * 全ページ共通のキーボードショートカット（UI を持たないリスナー）。
 * App.tsx の <Routes> の手前に 1 つだけマウントする。
 *
 * - ⌥1: タスクボード（/taskboard）
 *       - プロジェクト内（先頭セグメントが UUID）からは、そのプロジェクトに
 *         絞り込んだタスクボード（/taskboard?project=<id>）を開く
 *       - それ以外からは横断タスクボードを開く
 *       - タスクボード表示中の再押下は タスク → スケジュール → 工数 の循環
 *         （CrossProjectTaskBoard 側で処理）
 * - ⌥2: プロジェクトリスト（/projects）
 *       既にプロジェクトリスト表示中の再押下は、ホイールの選択を次のプロジェクトへ進める
 * - ⌥[ / ⌥]: 前 / 次のプロジェクトへ切り替え（プロジェクト内、および単一
 *       プロジェクトに絞り込んだタスクボード /taskboard?project=<id> のみ）。
 *       プロジェクトリストへ戻らずに、現在の画面種別（?tab= / タスクボード）を
 *       保ったまま隣のプロジェクトへ移動する。並びは一覧 API と同じ最終更新日
 *       降順で、両端は循環する。タスクボード上ではタスクボードを共有されて
 *       いるプロジェクトだけを巡回する（開いても空になる先へ飛ばないため）。
 *       プレビュー（/:projectId/:shotId）表示中は shotId がプロジェクト固有の
 *       ため無効（⌥←→ のテイク切替との誤操作も避ける）。
 *       角括弧は JIS 配列基準で判定するため、US 配列では ⌥] が「前へ」に
 *       倒れる。配列に依存しない ⌥, / ⌥. を同じ用途で使える（keyboardShortcuts
 *       の getAltProjectDirection を参照）。
 *
 * プロジェクト内タブ系（⌥3〜9）は対象プロジェクトの文脈が必要なため、
 * ProjectWrapper 側の useProjectShortcuts が担当する。
 * 一覧はヘッダー「?」の ShortcutGuideDrawer に掲載している。
 */
const GlobalShortcutListener = () => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { data: currentUser } = useGetCurrentUserQuery();
  const role = currentUser?.data?.role;
  // PROJECT メニューを持つロールのみ有効（GlobalHeader の menuItems と同基準）
  const enabled = role === "ADMIN" || role === "CREATOR" || role === "CLIENT";

  // ⌥[ / ⌥] 用の現在プロジェクト判定。プレビュー（第 2 セグメントあり）は対象外
  const segments = pathname.split("/").filter(Boolean);
  const projectPageId =
    segments.length === 1 && isUuid(segments[0]) ? segments[0] : null;

  // 単一プロジェクトに絞り込んだタスクボード（プロジェクト内から ⌥1 で開く画面）。
  // ここでもプロジェクト間を移動できるようにする。
  const taskBoardProjectId = useMemo(() => {
    if (pathname !== "/taskboard") return null;
    const value = new URLSearchParams(search).get("project");
    return value && isUuid(value) ? value : null;
  }, [pathname, search]);

  const currentProjectId = projectPageId ?? taskBoardProjectId;

  // 切り替え先の候補一覧。プロジェクトリスト（Projects.tsx）と同じ引数で
  // 購読して RTK Query のキャッシュを共有する（追加リクエストは実質発生しない）。
  // 並びは API の最終更新日降順。
  const { data: projectsData } = useGetProjectsQuery(
    { status: "active", limit: ACTIVE_PROJECTS_LIMIT },
    { skip: !enabled || !currentProjectId },
  );
  const allProjects = projectsData?.data?.projects;

  // タスクボード上では、タスクボードを共有されているプロジェクトだけを巡回する
  // （CrossProjectTaskBoard の accessibleProjects と同基準）。
  const projects = useMemo(() => {
    if (!allProjects) return allProjects;
    if (!taskBoardProjectId) return allProjects;
    return allProjects.filter((p) => p.task_board_access !== "none");
  }, [allProjects, taskBoardProjectId]);

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // ⌥[ / ⌥]（= ⌥, / ⌥.）: 現在のタブを保ったまま前 / 次のプロジェクトへ
      const projectDirection = getAltProjectDirection(e);
      if (projectDirection !== null) {
        if (!currentProjectId || !projects || projects.length === 0) return;
        if (isTypingTarget(e.target)) return;

        const currentIndex = projects.findIndex(
          (p) => p.project_id === currentProjectId,
        );
        // 一覧に無いプロジェクト（完了済み等）から押した場合は、
        // 次 = 先頭 / 前 = 末尾 から巡回に合流する
        const nextIndex =
          currentIndex === -1
            ? projectDirection === 1
              ? 0
              : projects.length - 1
            : (currentIndex + projectDirection + projects.length) %
              projects.length;
        const next = projects[nextIndex];
        if (!next || next.project_id === currentProjectId) return;

        e.preventDefault();
        if (taskBoardProjectId) {
          // タスクボードは開いたまま対象プロジェクトだけを差し替える
          navigate(`/taskboard?project=${next.project_id}`);
          return;
        }
        // ?tab= だけ引き継ぐ（thread 等のプロジェクト固有クエリは落とす）。
        // 切替先で権限が無いタブは ProjectWrapper / 各ページ側の表示制御に任せる。
        const tab = new URLSearchParams(search).get("tab");
        navigate(
          `/${next.project_id}${tab ? `?tab=${encodeURIComponent(tab)}` : ""}`,
        );
        return;
      }

      const digit = getAltDigit(e);
      if (digit !== 1 && digit !== 2) return;
      if (isTypingTarget(e.target)) return;

      e.preventDefault();
      if (digit === 1) {
        if (pathname === "/taskboard") {
          dispatchShortcutAction("taskboard-cycle");
        } else {
          // プロジェクト内からは、そのプロジェクトに絞り込んだタスクボードを開く
          const segment = pathname.split("/")[1] ?? "";
          if (isUuid(segment)) {
            navigate(`/taskboard?project=${segment}`);
          } else {
            navigate("/taskboard");
          }
        }
      } else {
        if (pathname === "/projects") {
          dispatchShortcutAction("projects-next");
        } else {
          navigate("/projects");
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    enabled,
    navigate,
    pathname,
    search,
    currentProjectId,
    taskBoardProjectId,
    projects,
  ]);

  return null;
};

export default GlobalShortcutListener;
